'use client';

import type { TooltipRenderProps } from 'react-joyride';

import { Button } from '../button';
import { DatumLabel, DatumValue } from '../datum';

/**
 * Entrance animation for the tooltip card. Scoped by the `artic-tour-tooltip`
 * class and neutral-named so it can't clash with other injected keyframes.
 * Mirrors the FloatingBar easing (`cubic-bezier(0.22, 1, 0.36, 1)`) with a
 * `prefers-reduced-motion` branch.
 */
const ARTIC_TOUR_TOOLTIP_CSS = `
@keyframes artic-tour-tooltip-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
.artic-tour-tooltip { animation: artic-tour-tooltip-in 200ms cubic-bezier(0.22, 1, 0.36, 1) both; }
@media (prefers-reduced-motion: reduce) { .artic-tour-tooltip { animation: none; } }
`;

/**
 * The Artic Industrial tooltip for {@link ArticTour}. Implements react-joyride's
 * {@link TooltipRenderProps} so it fully owns the tooltip markup: a left accent
 * rail (the SelectedRowMenu / FloatingBar motif), a small-caps `DatumLabel`
 * eyebrow, a tabular-mono `DatumValue` step counter (`02 / 05`), and the shared
 * `Button` variants for the footer actions.
 *
 * Which footer buttons render is driven by `step.buttons` (react-joyride's
 * per-step `buttons` option), so a step can opt out of Back/Skip without this
 * component changing.
 */
export function ArticTourTooltip({
  backProps,
  index,
  isLastStep,
  primaryProps,
  size,
  skipProps,
  step,
  tooltipProps,
}: TooltipRenderProps) {
  const showSkip = step.buttons.includes('skip') && !isLastStep;
  const showBack = step.buttons.includes('back') && index > 0;
  const showPrimary = step.buttons.includes('primary');

  return (
    <div
      {...tooltipProps}
      style={{ width: step.width ?? 360 }}
      className="artic-tour-tooltip bg-background text-foreground pointer-events-auto flex overflow-hidden rounded-md border shadow-lg"
    >
      <style>{ARTIC_TOUR_TOOLTIP_CSS}</style>

      {/* Accent status rail — the Artic Industrial floating-surface motif. */}
      <div aria-hidden className="bg-accent-rail w-1 shrink-0 self-stretch" />

      <div className="flex min-w-0 flex-1 flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <DatumLabel>Guided tour</DatumLabel>
            {step.title ? (
              <h2 className="text-sm leading-tight font-semibold">{step.title}</h2>
            ) : null}
          </div>
          <DatumValue
            size="sm"
            aria-label={`Step ${index + 1} of ${size}`}
            className="text-muted-foreground shrink-0 whitespace-nowrap"
          >
            {String(index + 1).padStart(2, '0')} / {String(size).padStart(2, '0')}
          </DatumValue>
        </div>

        <div className="text-muted-foreground text-sm leading-relaxed">{step.content}</div>

        <div className="flex items-center justify-between gap-2 pt-1">
          {showSkip ? (
            <Button
              {...skipProps}
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground tracking-wide uppercase"
            >
              Skip tour
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            {showBack ? (
              <Button {...backProps} type="button" variant="outline" size="sm">
                Back
              </Button>
            ) : null}
            {showPrimary ? (
              <Button {...primaryProps} type="button" size="sm" className="tracking-wide uppercase">
                {isLastStep ? 'Done' : 'Next'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
